/**
 * Search Results Page
 * Displays global search results with filtering and pagination
 */

import { useState, useEffect } from 'react'
import { useSearchParams, Link as RouterLink } from 'react-router-dom'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Container,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Stack,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material'
import {
  SearchOff as SearchOffIcon,
  Explore as ExploreIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  FilterAlt as FilterIcon,
  KeyboardArrowDown as ArrowDownIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'
import { useGlobalSearch } from '../hooks/useGlobalSearch'
import { SearchEntityType } from '../../../api/types/search'
import { AdventureResultCard } from '../components/AdventureResultCard'
import { UserResultCard } from '../components/UserResultCard'
import { GroupResultCard } from '../components/GroupResultCard'

// Page size options
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export function SearchResultsPage() {
  usePageTitle('Search')
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') || ''

  // Advanced filter params
  const categoriesParam = searchParams.get('categories')
  const tagsParam = searchParams.get('tags')
  const minRatingParam = searchParams.get('minRating')
  const maxRatingParam = searchParams.get('maxRating')
  const hideAdventuresParam = searchParams.get('hideAdventures')

  const [entityType, setEntityType] = useState<SearchEntityType>(() => {
    // If hideAdventures is set, start with User filter (so it shows users and groups but not adventures)
    if (hideAdventuresParam === 'true') {
      return SearchEntityType.User
    }
    return SearchEntityType.All
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [pageSizeMenuAnchor, setPageSizeMenuAnchor] = useState<null | HTMLElement>(null)

  // Calculate skip value for pagination
  const skip = (page - 1) * pageSize

  // Check if advanced filters are active
  const hasAdvancedFilters = !!(categoriesParam || tagsParam || minRatingParam || maxRatingParam)

  // Fetch search results
  const { data, isLoading, error } = useGlobalSearch({
    query,
    entityType: entityType === SearchEntityType.All ? undefined : entityType,
    skip,
    take: pageSize,
    enabled: query.length > 0,
  })

  // Reset page when query, filter, or page size changes
  useEffect(() => {
    setPage(1)
  }, [query, entityType, categoriesParam, tagsParam, minRatingParam, maxRatingParam])

  // Get filtered results based on entity type and advanced filters
  const getFilteredResults = () => {
    if (!data) return []

    const allResults: Array<{ type: 'adventure' | 'user' | 'group'; data: any }> = []

    // Add adventures with optional filtering
    if (entityType === SearchEntityType.All || entityType === SearchEntityType.Adventure) {
      let adventures = data.adventures

      // Filter by categories if specified
      if (categoriesParam) {
        const selectedCategories = categoriesParam.split(',').map(Number)
        adventures = adventures.filter(adv =>
          selectedCategories.includes(adv.mainCategory)
        )
      }

      // Filter by tags if specified
      if (tagsParam) {
        const selectedTags = tagsParam.split(',')
        adventures = adventures.filter(adv =>
          selectedTags.some(tag => adv.tags.includes(tag))
        )
      }

      // Filter by rating if specified
      if (minRatingParam || maxRatingParam) {
        const minRating = minRatingParam ? parseFloat(minRatingParam) : 0
        const maxRating = maxRatingParam ? parseFloat(maxRatingParam) : 5
        adventures = adventures.filter(adv =>
          adv.averageRating >= minRating && adv.averageRating <= maxRating
        )
      }

      adventures.forEach((adventure) => {
        allResults.push({ type: 'adventure', data: adventure })
      })
    }

    // Add users
    if (entityType === SearchEntityType.All || entityType === SearchEntityType.User) {
      data.users.forEach((user) => {
        allResults.push({ type: 'user', data: user })
      })
    }

    // Add groups
    if (entityType === SearchEntityType.All || entityType === SearchEntityType.Group) {
      data.groups.forEach((group) => {
        allResults.push({ type: 'group', data: group })
      })
    }

    return allResults
  }

  const filteredResults = getFilteredResults()
  const totalPages = Math.ceil((data?.totalResults || 0) / pageSize)

  const handleEntityTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: SearchEntityType | null) => {
    if (newType !== null) {
      setEntityType(newType)
    }
  }

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Show empty state if no query
  if (!query) {
    return (
      <Container maxWidth="md" sx={{ py: 8, px: { xs: 0, sm: 3 } }}>
        <Box sx={{ textAlign: 'center' }}>
          <SearchOffIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Start Searching
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Use the search bar above to find adventures, users, and groups
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Search Results
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Showing results for "{query}"
        </Typography>
      </Box>

      {/* Filter Bar */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
            {/* Entity Type Filter */}
            <ToggleButtonGroup
              value={entityType}
              exclusive
              onChange={handleEntityTypeChange}
              size="small"
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value={SearchEntityType.All}>
                All
              </ToggleButton>
              <ToggleButton value={SearchEntityType.Adventure}>
                <ExploreIcon sx={{ mr: 0.5 }} fontSize="small" />
                Adventures
              </ToggleButton>
              <ToggleButton value={SearchEntityType.User}>
                <PeopleIcon sx={{ mr: 0.5 }} fontSize="small" />
                Users
              </ToggleButton>
              <ToggleButton value={SearchEntityType.Group}>
                <GroupsIcon sx={{ mr: 0.5 }} fontSize="small" />
                Groups
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Advanced Search Button */}
            <Button
              component={RouterLink}
              to="/search/advanced"
              variant="outlined"
              startIcon={<FilterIcon />}
              size="small"
            >
              Advanced
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            {/* Results Count */}
            {data && (
              <Typography variant="body2" color="text.secondary">
                {data.totalResults} result{data.totalResults !== 1 ? 's' : ''}
              </Typography>
            )}

            {/* Active Filters Indicator */}
            {hasAdvancedFilters && (
              <Chip label="Advanced filters active" size="small" color="info" variant="outlined" />
            )}

            {/* Results Per Page - Compact Chip Style */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Show:
              </Typography>
              <Chip
                label={pageSize}
                onClick={(e) => setPageSizeMenuAnchor(e.currentTarget)}
                onDelete={(e) => setPageSizeMenuAnchor(e.currentTarget as any)}
                deleteIcon={<ArrowDownIcon />}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
              />
              <Menu
                anchorEl={pageSizeMenuAnchor}
                open={Boolean(pageSizeMenuAnchor)}
                onClose={() => setPageSizeMenuAnchor(null)}
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <MenuItem
                    key={size}
                    selected={size === pageSize}
                    onClick={() => {
                      setPageSize(size)
                      setPageSizeMenuAnchor(null)
                    }}
                  >
                    {size} per page
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Stack>
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || 'An error occurred while searching. Please try again.'}
        </Alert>
      )}

      {/* Results */}
      {!isLoading && !error && data && (
        <>
          {/* No Results */}
          {filteredResults.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SearchOffIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No Results Found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your search query or filters
              </Typography>
            </Box>
          )}

          {/* Results List */}
          {filteredResults.length > 0 && (
            <Box>
              {filteredResults.map((result, index) => {
                const key = `${result.type}-${result.data.id}-${index}`

                switch (result.type) {
                  case 'adventure':
                    return <AdventureResultCard key={key} adventure={result.data} />
                  case 'user':
                    return <UserResultCard key={key} user={result.data} />
                  case 'group':
                    return <GroupResultCard key={key} group={result.data} />
                  default:
                    return null
                }
              })}

              {/* Compact Pagination Bar */}
              {totalPages > 1 && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 4,
                  gap: 1,
                  flexWrap: 'wrap'
                }}>
                  {/* First Page */}
                  <IconButton
                    size="small"
                    onClick={(e) => handlePageChange(e, 1)}
                    disabled={page === 1}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <FirstPageIcon fontSize="small" />
                  </IconButton>

                  {/* Previous Page */}
                  <IconButton
                    size="small"
                    onClick={(e) => handlePageChange(e, page - 1)}
                    disabled={page === 1}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  {/* Page Numbers */}
                  {(() => {
                    const pages: number[] = []
                    const maxVisible = 5

                    if (totalPages <= maxVisible) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i)
                      }
                    } else {
                      // Smart pagination: show first, last, current, and nearby pages
                      if (page <= 3) {
                        // Near start
                        pages.push(1, 2, 3, 4, 5)
                      } else if (page >= totalPages - 2) {
                        // Near end
                        for (let i = totalPages - 4; i <= totalPages; i++) {
                          pages.push(i)
                        }
                      } else {
                        // Middle
                        pages.push(page - 1, page, page + 1)
                      }
                    }

                    return pages.map(pageNum => (
                      <Chip
                        key={pageNum}
                        label={pageNum}
                        onClick={(e) => handlePageChange(e as any, pageNum)}
                        color={page === pageNum ? 'primary' : 'default'}
                        variant={page === pageNum ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                          minWidth: 36,
                          cursor: 'pointer',
                          fontWeight: page === pageNum ? 'bold' : 'normal'
                        }}
                      />
                    ))
                  })()}

                  {/* Next Page */}
                  <IconButton
                    size="small"
                    onClick={(e) => handlePageChange(e, page + 1)}
                    disabled={page === totalPages}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>

                  {/* Last Page */}
                  <IconButton
                    size="small"
                    onClick={(e) => handlePageChange(e, totalPages)}
                    disabled={page === totalPages}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <LastPageIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Container>
  )
}
